import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  NAV_GUARD_SWIPE_EVENT,
  type NavGuardSwipeDetail,
} from "../utils/installGlobalSwipeBlock";
import {
  consumeHistoryGuardProgrammaticPopSuppression,
  ensureHistoryBackTrap,
} from "../utils/historyBackGuard";

type PendingSource =
  | { kind: "swipe"; intent: NavGuardSwipeDetail["intent"] }
  | { kind: "history" };

/**
 * Confirms before leaving via horizontal swipe / trackpad or the browser back button.
 * useBlocker requires a data router; BrowserRouter uses a history trap + popstate instead.
 *
 * @param enabled - Set to false on auth/public pages (login, OTP, forgot-password, …).
 *   When false, the history trap pushState is skipped entirely, so those pages are never
 *   recorded as extra entries in the browser history stack.
 */
const useGlobalNavigationGuard = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState<PendingSource | null>(null);
  const location = useLocation();

  const isOpenRef = useRef(false);
  const pendingRef = useRef<PendingSource | null>(null);
  const skipNextPopRef = useRef(false);
  const currentUrlRef = useRef(window.location.href);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  // Install the duplicate-entry trap only on protected pages.
  // Running on auth pages (login, OTP, …) would push those URLs into the
  // browser history stack, making the Back button return to them after login.
  // Reinstalls on every route change so the trap covers each navigated route.
  useEffect(() => {
    if (!enabled) return;
    currentUrlRef.current = window.location.href;
    ensureHistoryBackTrap();
  }, [location.pathname, location.search, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onSwipeIntent = (ev: Event) => {
      if (isOpenRef.current) return;
      const ce = ev as CustomEvent<NavGuardSwipeDetail>;
      const intent = ce.detail?.intent ?? "back";
      setPending({ kind: "swipe", intent });
      setIsOpen(true);
    };

    window.addEventListener(NAV_GUARD_SWIPE_EVENT, onSwipeIntent);
    return () => window.removeEventListener(NAV_GUARD_SWIPE_EVENT, onSwipeIntent);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onPopState = () => {
      if (consumeHistoryGuardProgrammaticPopSuppression()) {
        return;
      }
      if (skipNextPopRef.current) {
        skipNextPopRef.current = false;
        return;
      }
      // Push back the URL the user was on BEFORE the back gesture.
      // This listener runs in the capture phase — before React Router's
      // bubble-phase popstate listener — so by the time React Router reads
      // window.location it sees the original URL and does not re-render the
      // previous route. This prevents the "page flips first, dialog second"
      // problem regardless of whether a per-route trap entry exists.
      window.history.pushState(
        { __mgmNavGuardTrap: true },
        "",
        currentUrlRef.current,
      );
      if (isOpenRef.current) return;
      setPending({ kind: "history" });
      setIsOpen(true);
    };

    // capture: true — fires before all bubble-phase listeners (including React Router).
    window.addEventListener("popstate", onPopState, { capture: true });
    return () => window.removeEventListener("popstate", onPopState, { capture: true });
  }, [enabled]);

  const onConfirm = useCallback(() => {
    const current = pendingRef.current;
    setIsOpen(false);
    setPending(null);

    if (current?.kind === "swipe") {
      skipNextPopRef.current = true;
      if (current.intent === "back") {
        window.history.back();
      } else {
        window.history.forward();
      }
      return;
    }

    if (current?.kind === "history") {
      // With replace-based internal navigation the browser stack looks like:
      //   [...previous-app-entries, module-entry, trap-entry]
      // We are currently at "module-entry" (the guard fired a pushState to
      // create the new trap-entry, so position is now at trap-entry).
      // go(-2) skips both the trap AND the module entry, landing on the last
      // previous-app entry — which is exactly what we want.
      // If there is no previous app (user opened the module URL directly),
      // fall back to a single back() which stays within whatever is available.
      skipNextPopRef.current = true;
      if (window.history.length > 2) {
        window.history.go(-2);
      } else {
        window.history.back();
      }
    }
  }, []);

  const onCancel = useCallback(() => {
    setIsOpen(false);
    setPending(null);
  }, []);

  return { isOpen, onConfirm, onCancel };
};

export default useGlobalNavigationGuard;
